import React, { FC } from 'react'
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActionArea,
} from '@mui/material'
import { Sms, Email, Smartphone } from '@mui/icons-material'
import { Module, ModuleHeader, ModuleBody, CustomAlert } from 'components'
import useStyles from './mfa-factor-select-styles'

interface MfaFactorSelectProps {
  factors: any[]
  onSelectFactor: (factor: any) => void
  serviceError?: string
}

const getFactorIcon = (factorType: string) => {
  switch (factorType) {
    case 'sms':
      return <Sms fontSize="large" />
    case 'email':
      return <Email fontSize="large" />
    case 'token:software:totp':
    case 'totp':
      return <Smartphone fontSize="large" />
    default:
      return null
  }
}

const getFactorDisplayName = (factorType: string): string => {
  switch (factorType) {
    case 'sms':
      return 'Text Message (SMS)'
    case 'email':
      return 'Email'
    case 'token:software:totp':
    case 'totp':
      return 'Authenticator App'
    default:
      return factorType
  }
}

const getFactorDescription = (factor: any): string => {
  switch (factor.factorType) {
    case 'sms':
      return factor.profile?.phoneNumber
        ? `Send code to ${factor.profile.phoneNumber}`
        : 'Receive a code via text message'
    case 'email':
      return factor.profile?.email
        ? `Send code to ${factor.profile.email}`
        : 'Receive a code via email'
    case 'token:software:totp':
    case 'totp':
      return 'Use your authenticator app'
    default:
      return ''
  }
}

const MfaFactorSelect: FC<MfaFactorSelectProps> = ({
  factors,
  onSelectFactor,
  serviceError,
}) => {
  const { classes } = useStyles()

  // Filter to only show supported factor types
  const supportedFactors = factors.filter((f) =>
    ['sms', 'email', 'token:software:totp', 'totp'].includes(f.factorType),
  )

  return (
    <Module>
      <ModuleHeader title="Choose Verification Method">
        <Typography variant="body1" className={classes.subtitle}>
          Select how you would like to verify your identity.
        </Typography>
      </ModuleHeader>

      <ModuleBody>
        {serviceError && (
          <CustomAlert severity="error">{serviceError}</CustomAlert>
        )}

        <Box className={classes.factorsContainer}>
          {supportedFactors.map((factor) => (
            <Card key={factor.id} className={classes.factorCard}>
              <CardActionArea onClick={() => onSelectFactor(factor)}>
                <CardContent className={classes.cardContent}>
                  <Box className={classes.iconContainer}>
                    {getFactorIcon(factor.factorType)}
                  </Box>
                  <Box className={classes.textContainer}>
                    <Typography variant="h6" className={classes.factorName}>
                      {getFactorDisplayName(factor.factorType)}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      className={classes.factorDescription}
                    >
                      {getFactorDescription(factor)}
                    </Typography>
                  </Box>
                </CardContent>
              </CardActionArea>
            </Card>
          ))}
        </Box>
      </ModuleBody>
    </Module>
  )
}

export default MfaFactorSelect
